import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Chip, Stack } from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleHeaderAction,
  ConsoleSearchFilter,
  ConsoleTable,
  useQueryInitialFilters,
} from 'components/console';
import {
  CsvBulkUploadDialog,
  CsvTemplateDownloadDialog,
  downloadCsvTemplate,
} from 'components/csv';
import { CsvBulkConfig } from 'components/csv/types';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import {
  MasterTypeResult,
  getContractType,
  getEmploymentType,
  getGender,
} from 'stores/MasterTypeStoreAdapter';
import {
  CreateMemberParams,
  MemberDetailResult,
  MemberListResult,
  deleteMember,
  getMember,
  listMember,
} from 'stores/MemberStoreAdapter';
import { PartnerResult, listPartner } from 'stores/PartnerStoreAdapter';
import { UserResult, getUser } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

import MemberCreateForm, { MemberCreateFormHandle } from './components/MemberCreateForm';
import MemberUpdateForm, { MemberUpdateFormHandle } from './components/MemberUpdateForm';
import { createMemberBulkConfig, createMemberCsvFields } from './components/memberCsvConfig';

type DrawerMode = 'none' | 'create' | 'detail';

const MemberManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [isProgress, setProgress] = useState(true);
  const [members, setMembers] = useState<MemberListResult[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<MasterTypeResult[]>([]);
  const [genders, setGenders] = useState<MasterTypeResult[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
  const [partners, setPartners] = useState<PartnerResult[]>([]);
  const [users, setUsers] = useState<UserResult[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberDetailResult | null>(null);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('none');
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCsvTemplateDialogOpen, setIsCsvTemplateDialogOpen] = useState(false);
  const [isCsvBulkDialogOpen, setIsCsvBulkDialogOpen] = useState(false);
  // 現在適用中の検索パラメータ（作成/更新/削除/CSV後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<Record<string, string | boolean>>({});

  const createFormRef = useRef<MemberCreateFormHandle>(null);
  const updateFormRef = useRef<MemberUpdateFormHandle>(null);

  useEffect(() => {
    Promise.all([getEmploymentType(), getGender(), getContractType(), listPartner({}), getUser({})])
      .then(([empTypes, genTypes, conTypes, partnerList, userList]) => {
        setEmploymentTypes(empTypes);
        setGenders(genTypes);
        setContractTypes(conTypes);
        setPartners(partnerList);
        setUsers(userList);
      })
      .catch(() => {});
  }, []);

  const fetchMembers = (params: Record<string, string | boolean>) => {
    setProgress(true);
    listMember(params)
      .then((result: MemberListResult[]) => {
        setMembers(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('要員情報の取得に失敗しました');
      });
  };

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchMembers(activeParams);

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelectedMember(null);
    setIsEditingMember(false);
  };

  // 作成・更新後は共通で「フォームを閉じて現在の検索条件で再検索」
  const handleCreated = () => {
    closeDrawer();
    refetch();
  };

  const handleMemberUpdated = () => {
    closeDrawer();
    refetch();
  };

  const handleDeleteConfirm = () => {
    if (!selectedMember) return;
    setIsDeleteDialogOpen(false);
    setProgress(true);
    deleteMember({ memberId: selectedMember.memberId })
      .then(() => {
        successDefault('要員を削除しました');
        closeDrawer();
        refetch();
      })
      .catch((e: Error) => {
        errorDefault('要員の削除に失敗しました', e.message);
      })
      .finally(() => setProgress(false));
  };

  const handleSearch = (filters: Filter[]) => {
    closeDrawer();
    const params: Record<string, string> = {};
    filters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    setActiveParams(params);
    fetchMembers(params);
  };

  const handleCreateClick = () => {
    setSelectedMember(null);
    setIsEditingMember(false);
    setDrawerMode('create');
  };

  const handleRowClick = (row: MemberListResult) => {
    setIsEditingMember(false);
    setProgress(true);
    getMember({ memberId: row.memberId })
      .then((detail) => {
        setSelectedMember(detail);
        setDrawerMode('detail');
      })
      .catch(() => errorDefault('要員詳細の取得に失敗しました'))
      .finally(() => setProgress(false));
  };

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'memberName', name: '要員名', type: 'text', color: '#78C1F3' },
      { key: 'memberNameKana', name: '要員名（カナ）', type: 'katakana-text', color: '#3A98B9' },
      {
        key: 'employmentTypeId',
        name: '雇用形態',
        type: 'select',
        color: '#B799FF',
        values: employmentTypes.map((t) => ({ key: t.typeCode, name: t.typeLabel })),
      },
      {
        key: 'contractTypeId',
        name: '契約形態',
        type: 'select',
        color: '#9DC08B',
        values: contractTypes.map((t) => ({ key: t.typeCode, name: t.typeLabel })),
      },
      {
        key: 'partnerId',
        name: 'パートナー',
        type: 'select',
        color: '#FF8A65',
        values: partners.map((p) => ({ key: p.partnerId, name: p.partnerName })),
      },
      {
        key: 'isIndividual',
        name: '個人事業主',
        type: 'check',
        color: '#4DB6AC',
        value: { key: 'true', name: '個人事業主のみ' },
      },
      {
        key: 'includeDeleted',
        name: '削除済みを含む',
        type: 'check',
        color: '#78909C',
        value: { key: 'true', name: '削除済みを含む' },
      },
    ],
    [employmentTypes, contractTypes, partners],
  );

  // URL クエリパラメータを初期検索条件として反映
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions);
  useEffect(() => {
    setActiveParams(queryParams);
    fetchMembers(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const csvFields = useMemo(
    () => createMemberCsvFields(employmentTypes, genders, contractTypes, partners, users),
    [employmentTypes, genders, contractTypes, partners, users],
  );
  const csvBulkConfig: CsvBulkConfig<CreateMemberParams> | null = useMemo(
    () =>
      employmentTypes.length > 0 && genders.length > 0
        ? createMemberBulkConfig(employmentTypes, genders, contractTypes, partners, users)
        : null,
    [employmentTypes, genders, contractTypes, partners, users],
  );
  const handleCsvTemplateDownload = () => {
    downloadCsvTemplate(csvFields, '要員一括登録テンプレート.csv');
  };

  const menuActions: ConsoleHeaderAction[] = [
    {
      label: 'CSV一括登録',
      icon: <UploadFileIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvBulkDialogOpen(true),
    },
    {
      label: 'CSVテンプレートをダウンロード',
      icon: <FileDownloadIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvTemplateDialogOpen(true),
    },
  ];

  const contractTypeLabel = (id?: string) =>
    contractTypes.find((t) => t.typeCode === id)?.typeLabel ?? '';

  const columns: ConsoleColumn<MemberListResult>[] = [
    {
      key: 'memberName',
      header: '要員名',
      width: 200,
      ellipsis: (m) => m.memberName,
    },
    {
      key: 'kana',
      header: 'カナ',
      width: 170,
      ellipsis: (m) => m.memberNameKana,
    },
    {
      key: 'employment',
      header: '区分',
      width: 84,
      render: (m) => {
        const isEmployee = m.employmentTypeId === 'EMPLOYEE';
        return (
          <Chip
            label={isEmployee ? '社員' : '社外'}
            size="small"
            color={isEmployee ? 'primary' : 'secondary'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      key: 'affiliation',
      header: '所属',
      width: 180,
      ellipsis: (m) => m.partnerName || m.department || '',
    },
    {
      key: 'contractType',
      header: '契約形態',
      width: 120,
      ellipsis: (m) =>
        m.employmentTypeId === 'EMPLOYEE' ? '社員' : contractTypeLabel(m.contractTypeId),
    },
    {
      key: 'status',
      header: '状態',
      width: 80,
      render: (m) =>
        m.isDeleted ? (
          <Chip
            label="削除済"
            size="small"
            color="error"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            —
          </Box>
        ),
    },
  ];

  const isEmployeeSel = selectedMember?.employmentTypeId === 'EMPLOYEE';

  const drawerFooter =
    drawerMode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={closeDrawer} />
        <CustomButton
          label="作成"
          buttonVariant="primary"
          icon={<PersonAddIcon />}
          onClick={() => createFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedMember && !selectedMember.isDeleted && isEditingMember ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setIsEditingMember(false)}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={() => updateFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedMember && !selectedMember.isDeleted ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="削除"
          buttonVariant="danger"
          icon={<DeleteIcon />}
          onClick={() => setIsDeleteDialogOpen(true)}
        />
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={() => setIsEditingMember(true)}
        />
      </Stack>
    ) : undefined;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="要員"
        count={members.length}
        description="社員・外部要員の登録・更新・検索を行います"
        backLabel="要員管理"
        onBack={() => navigate('/member')}
        onRefresh={refetch}
        menuActions={menuActions}
        actions={
          <CustomButton
            label="要員を登録"
            buttonVariant="primary"
            icon={<PersonAddIcon />}
            onClick={handleCreateClick}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={members}
        rowKey={(m) => m.memberId}
        selectedKey={drawerMode === 'detail' ? selectedMember?.memberId : undefined}
        onRowClick={handleRowClick}
        emptyMessage="要員がありません。「要員を登録」または「CSV一括登録」から登録してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="member-list"
            userProfileKey="member-member-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="要員の登録"
        width={640}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MemberCreateForm
              ref={createFormRef}
              onCreate={handleCreated}
              onCancel={closeDrawer}
              setProgress={setProgress}
              hideHeader
            />
          </Box>
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
        </Box>
      </ConsoleDetailDrawer>

      <ConsoleDetailDrawer
        open={drawerMode === 'detail' && selectedMember !== null}
        onClose={closeDrawer}
        title={selectedMember?.memberName ?? ''}
        badges={
          selectedMember && (
            <>
              <Chip
                label={isEmployeeSel ? '社員' : '社外'}
                size="small"
                color={isEmployeeSel ? 'primary' : 'secondary'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              {selectedMember.isDeleted && (
                <Chip
                  label="削除済"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </>
          )
        }
        width={640}
      >
        {selectedMember && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <MemberUpdateForm
                ref={updateFormRef}
                member={selectedMember}
                isEditing={isEditingMember}
                onMemberUpdated={handleMemberUpdated}
                onClose={closeDrawer}
                onEditingChange={setIsEditingMember}
                setProgress={setProgress}
                hideHeader
              />
            </Box>
            {drawerFooter && (
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
            )}
          </Box>
        )}
      </ConsoleDetailDrawer>

      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="要員削除"
      >
        <Alert severity="warning">この操作は取り消せません。削除された要員は復元できません。</Alert>
      </DeleteConfirmDialog>

      <CsvTemplateDownloadDialog
        open={isCsvTemplateDialogOpen}
        onClose={() => setIsCsvTemplateDialogOpen(false)}
        entityName="要員"
        fields={csvFields}
        onDownload={handleCsvTemplateDownload}
      />

      {csvBulkConfig && (
        <CsvBulkUploadDialog
          open={isCsvBulkDialogOpen}
          onClose={() => setIsCsvBulkDialogOpen(false)}
          config={csvBulkConfig}
          onComplete={() => {
            closeDrawer();
            refetch();
          }}
        />
      )}

      <Circular open={isProgress} />
    </Box>
  );
};

export default MemberManagementPage;
